import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { alpha, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { TopBusinessUnit } from 'services/types/relifDashboard';

interface TopBusinessUnitsTableProps {
  data: TopBusinessUnit[];
  title: string;
}

/**
 * TopBusinessUnitsTable component for displaying top business units in a table
 *
 * @param {Object} props - The component props
 * @param {TopBusinessUnit[]} props.data - Array of top business units
 * @param {string} props.title - Title for the table
 * @returns {JSX.Element} The TopBusinessUnitsTable component
 */
const TopBusinessUnitsTable: React.FC<TopBusinessUnitsTableProps> = ({ data, title }) => {
  const theme = useTheme();

  const typeLabels: Record<string, string> = {
    car_dealership: 'Concesionaria',
    insurance_broker: 'Corredora de Seguros',
    financial_institution: 'Instituciones Financieras',
    real_estate: 'Inmobiliaria',
    other: 'Otro',
  };

  const typeColors: Record<string, string> = {
    car_dealership: theme.palette.primary.main,
    insurance_broker: theme.palette.secondary.main,
    financial_institution: theme.palette.success.main,
    real_estate: theme.palette.warning.main,
    other: theme.palette.info.main,
  };

  return (
    <Card sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Tipo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Leads
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, index) => (
                <TableRow
                  key={item.businessUnitId}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index < 3 ? 'primary.main' : alpha(theme.palette.primary.main, 0.1),
                        color: index < 3 ? 'primary.contrastText' : 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      {index + 1}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.businessUnitName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: alpha(typeColors[item.businessUnitType] || theme.palette.grey[500], 0.1),
                        color: typeColors[item.businessUnitType] || theme.palette.grey[700],
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {typeLabels[item.businessUnitType] || item.businessUnitType}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {item.leadsCount.toLocaleString('es-CL')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      No hay datos disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default TopBusinessUnitsTable;
